import { collection, getDocs, query, orderBy, deleteDoc } from 'firebase/firestore';
import { getDb, shouldUseLocalStorage } from '../firebase';
import { isDevelopment } from '../utils/env';
import config from '../config';
import { ResultData } from '../types';

// Version check
console.log('LoadSurvey Module Version: 2.0.0 - Production Mode');

// Add error type
interface ErrorWithDetails {
  name?: string;
  message?: string;
  stack?: string;
}

/**
 * Loads survey results from either localStorage (development) or Firestore (production)
 * @returns Promise<ResultData[]> Array of survey results
 */
export const loadSurveyResults = async (): Promise<ResultData[]> => {
  console.log('\n');
  console.log('==========================================');
  console.log('🔍 LOADING SURVEY RESULTS');
  console.log('==========================================');
  console.log('📱 ENVIRONMENT:', shouldUseLocalStorage() ? 'DEVELOPMENT' : 'PRODUCTION');
  console.log('💾 STORAGE SOURCE:', shouldUseLocalStorage() ? 'LOCALSTORAGE' : 'FIRESTORE');
  console.log('📊 COLLECTION:', config.app.resultsCollection);
  console.log('🔐 AUTH STATUS:', isDevelopment() ? 'Development Mode' : 'Production Mode');
  console.log('==========================================\n');
  
  try {
    // In development, use localStorage
    if (shouldUseLocalStorage()) {
      console.log('📂 ATTEMPTING TO LOAD FROM LOCALSTORAGE...');
      const savedData = localStorage.getItem('zodiacResultsData');
      
      if (!savedData) {
        console.log('❌ NO DATA FOUND IN LOCALSTORAGE AT KEY: zodiacResultsData');
        return [];
      }
      
      try {
        const parsedData = JSON.parse(savedData);
        console.log(`✅ SUCCESSFULLY LOADED ${parsedData.length} RESULTS FROM LOCALSTORAGE`);
        console.log('📅 FIRST RESULT TIMESTAMP:', parsedData[0]?.timestamp);
        console.log('📅 LAST RESULT TIMESTAMP:', parsedData[parsedData.length - 1]?.timestamp);
        return parsedData;
      } catch (e) {
        console.error('❌ ERROR PARSING LOCALSTORAGE DATA:', e);
        console.error('📄 RAW DATA:', savedData);
        return [];
      }
    }

    // In production, use Firestore
    if (!getDb()) {
      console.error('❌ FIRESTORE NOT INITIALIZED - DB OBJECT IS NULL');
      console.error('Environment:', isDevelopment() ? 'Development' : 'Production');
      console.error('Storage Type:', config.app.storage.type);
      console.error('Force Type:', config.app.storage.forceType);
      return [];
    }

    console.log('📂 ATTEMPTING TO LOAD FROM FIRESTORE...');
    console.log('🔌 DATABASE INSTANCE:', getDb() ? 'INITIALIZED' : 'NOT INITIALIZED');
    console.log('📊 COLLECTION NAME:', config.app.resultsCollection);
    console.log('🔐 AUTH STATUS:', isDevelopment() ? 'Development Mode' : 'Production Mode');
    
    const resultsRef = collection(getDb(), config.app.resultsCollection);
    console.log('📊 COLLECTION REFERENCE CREATED');
    
    const resultsQuery = query(resultsRef, orderBy('timestamp', 'desc'));
    console.log('🔍 QUERY CREATED WITH TIMESTAMP ORDERING');
    
    console.log('⏳ EXECUTING FIRESTORE QUERY...');
    const querySnapshot = await getDocs(resultsQuery);
    console.log(`📥 QUERY RETURNED ${querySnapshot.size} DOCUMENTS`);
    
    if (querySnapshot.size > 0) {
      querySnapshot.docs.forEach((doc, index) => {
        console.log(`📄 PROCESSING DOCUMENT ${doc.id} WITH TIMESTAMP: ${doc.data().timestamp}`);
      });
    }
    
    const results = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as ResultData));
    
    console.log(`✅ SUCCESSFULLY PROCESSED ${results.length} RESULTS FROM FIRESTORE`);
    if (results.length > 0) {
      console.log('📅 FIRST RESULT TIMESTAMP:', results[0].timestamp);
      console.log('📅 LAST RESULT TIMESTAMP:', results[results.length - 1].timestamp);
    }
    
    // Cache results in localStorage for development mode
    if (shouldUseLocalStorage()) {
      console.log('💾 CACHING RESULTS IN LOCALSTORAGE FOR DEVELOPMENT');
      try {
        localStorage.setItem('zodiacResultsData', JSON.stringify(results));
        console.log('✅ SUCCESSFULLY CACHED RESULTS IN LOCALSTORAGE');
      } catch (e) {
        console.error('❌ ERROR CACHING RESULTS IN LOCALSTORAGE:', e);
      }
    }
    
    return results;
  } catch (error) {
    console.error('❌ ERROR LOADING RESULTS:', error);
    console.error('Environment:', isDevelopment() ? 'Development' : 'Production');
    console.error('Storage Type:', config.app.storage.type);
    console.error('Force Type:', config.app.storage.forceType);
    return [];
  }
};

/**
 * Clears all survey results from storage
 * @returns Promise<void>
 */
export const clearSurveyResults = async (): Promise<void> => {
  console.log('\n');
  console.log('==========================================');
  console.log('🗑️ CLEARING SURVEY RESULTS');
  console.log('==========================================');
  console.log('📱 ENVIRONMENT:', shouldUseLocalStorage() ? 'DEVELOPMENT' : 'PRODUCTION');
  console.log('💾 STORAGE SOURCE:', shouldUseLocalStorage() ? 'LOCALSTORAGE' : 'FIRESTORE');
  console.log('==========================================\n');
  
  try {
    if (shouldUseLocalStorage()) {
      console.log('🗑️ ATTEMPTING TO CLEAR LOCALSTORAGE DATA...');
      const beforeClear = localStorage.getItem('zodiacResultsData');
      console.log('📊 DATA EXISTS BEFORE CLEAR:', !!beforeClear);
      
      localStorage.removeItem('zodiacResultsData');
      
      const afterClear = localStorage.getItem('zodiacResultsData');
      console.log('📊 DATA EXISTS AFTER CLEAR:', !!afterClear);
      console.log('✅ LOCALSTORAGE CLEARED SUCCESSFULLY');
      return;
    }

    if (!getDb()) {
      console.error('❌ FIRESTORE NOT INITIALIZED - DB OBJECT IS NULL');
      return;
    }

    console.log('🗑️ ATTEMPTING TO CLEAR FIRESTORE DATA...');
    const resultsRef = collection(getDb(), config.app.resultsCollection);
    console.log('📊 COLLECTION REFERENCE CREATED');
    
    console.log('🔍 FETCHING DOCUMENTS TO DELETE...');
    const querySnapshot = await getDocs(resultsRef);
    
    if (querySnapshot.empty) {
      console.log('ℹ️ NO DOCUMENTS FOUND TO DELETE');
      return;
    }

    console.log(`📥 FOUND ${querySnapshot.size} DOCUMENTS TO DELETE`);
    console.log('📄 DOCUMENT IDs:', querySnapshot.docs.map(doc => doc.id));
    
    // Delete each document
    console.log('🗑️ STARTING DOCUMENT DELETION...');
    const deletePromises = querySnapshot.docs.map(doc => {
      console.log(`🗑️ DELETING DOCUMENT ${doc.id}`);
      return deleteDoc(doc.ref);
    });
    
    await Promise.all(deletePromises);
    console.log('✅ ALL DOCUMENTS DELETED SUCCESSFULLY');
    
    // Verify deletion
    const verifySnapshot = await getDocs(resultsRef);
    console.log('🔍 VERIFICATION - DOCUMENTS REMAINING:', verifySnapshot.size);
  } catch (error) {
    console.error('❌ ERROR CLEARING SURVEY RESULTS:', error);
    console.error('🔍 ERROR DETAILS:', {
      name: (error as ErrorWithDetails).name,
      message: (error as ErrorWithDetails).message,
      stack: (error as ErrorWithDetails).stack
    });
    throw error;
  }
}; 